import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface DirectMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  message: string;
  created_at: string;
  edited_at?: string | null;
  deleted_at?: string | null;
  hidden_for?: string[] | null;
  sender?: {
    first_name: string | null;
    avatar_url: string | null;
  };
}

export function useDirectMessages(conversationId: string) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<DirectMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const profileCache = useRef<Map<string, { first_name: string | null; avatar_url: string | null }>>(new Map());

  const fetchProfile = async (userId: string) => {
    if (profileCache.current.has(userId)) return profileCache.current.get(userId)!;
    const { data } = await supabase
      .from("public_profiles")
      .select("first_name, avatar_url")
      .eq("id", userId)
      .single();
    const profile = { first_name: data?.first_name || null, avatar_url: data?.avatar_url || null };
    profileCache.current.set(userId, profile);
    return profile;
  };

  const fetchMessages = useCallback(async () => {
    if (!conversationId) return;
    setLoading(true);

    const { data, error } = await supabase
      .from("direct_messages")
      .select("*")
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Error fetching messages:", error);
      setLoading(false);
      return;
    }

    // Fetch unique sender profiles
    const senderIds = [...new Set((data || []).map((m) => m.sender_id))];
    await Promise.all(senderIds.map(fetchProfile));

    const enriched: DirectMessage[] = (data || [])
      .filter((m) => !m.hidden_for?.includes(user?.id ?? ""))
      .map((m) => ({
        ...m,
        sender: profileCache.current.get(m.sender_id),
      }));

    setMessages(enriched);
    setLoading(false);
  }, [conversationId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Mark conversation as read
  const markRead = useCallback(async () => {
    if (!user || !conversationId) return;
    await supabase
      .from("conversation_participants")
      .update({ last_read_at: new Date().toISOString() })
      .eq("conversation_id", conversationId)
      .eq("user_id", user.id);
    // Notify conversation list to clear unread badge (realtime UPDATE on
    // conversation_participants isn't in the publication).
    try {
      window.dispatchEvent(
        new CustomEvent("conversation-read", { detail: { conversationId } })
      );
    } catch {}
  }, [user, conversationId]);

  // Realtime subscription
  useEffect(() => {
    if (!conversationId) return;

    const channel = supabase
      .channel(`dm-${conversationId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "direct_messages",
          filter: `conversation_id=eq.${conversationId}`,
        },
        async (payload) => {
          const newMsg = payload.new as any;
          if (newMsg.hidden_for?.includes(user?.id ?? "")) return;
          // Avoid duplicates
          setMessages((prev) => {
            if (prev.some((m) => m.id === newMsg.id)) return prev;
            const sender = profileCache.current.get(newMsg.sender_id);
            return [...prev, { ...newMsg, sender }];
          });
          // If not from current user, fetch profile if needed
          if (!profileCache.current.has(newMsg.sender_id)) {
            await fetchProfile(newMsg.sender_id);
          }
          // Auto mark as read
          markRead();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "direct_messages",
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const updated = payload.new as any;
          setMessages((prev) =>
            updated.hidden_for?.includes(user?.id ?? "")
              ? prev.filter((m) => m.id !== updated.id)
              : prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m))
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationId, markRead]);

  const sendMessage = async (text: string) => {
    if (!user || !conversationId || !text.trim()) return;

    // Optimistically add message to state
    const optimisticMsg: DirectMessage = {
      id: crypto.randomUUID(),
      conversation_id: conversationId,
      sender_id: user.id,
      message: text.trim(),
      created_at: new Date().toISOString(),
      sender: profileCache.current.get(user.id) || { first_name: null, avatar_url: null },
    };
    setMessages((prev) => [...prev, optimisticMsg]);

    const { error } = await supabase.from("direct_messages").insert({
      conversation_id: conversationId,
      sender_id: user.id,
      message: text.trim(),
    });

    if (error) {
      // Remove optimistic message on failure
      setMessages((prev) => prev.filter((m) => m.id !== optimisticMsg.id));
      console.error("Error sending message:", error);
      throw error;
    }

    // Fire-and-forget: send email notification to recipient
    try {
      const { data: participants } = await supabase
        .from("conversation_participants")
        .select("user_id")
        .eq("conversation_id", conversationId)
        .neq("user_id", user.id);

      if (participants && participants.length > 0) {
        const recipientId = participants[0].user_id;
        supabase.functions.invoke("send-dm-email", {
          body: {
            conversationId,
            recipientId,
            messagePreview: text.trim().substring(0, 200),
          },
        }).catch((err) => console.error("DM email notification error:", err));
      }
    } catch (emailErr) {
      console.error("DM email notification error:", emailErr);
    }
  };

  const editMessage = async (messageId: string, newText: string) => {
    if (!user || !newText.trim()) return;
    const trimmed = newText.trim();
    const previous = messages.find((m) => m.id === messageId);
    const editedAt = new Date().toISOString();

    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, message: trimmed, edited_at: editedAt } : m))
    );

    const { error } = await supabase
      .from("direct_messages")
      .update({ message: trimmed, edited_at: editedAt } as any)
      .eq("id", messageId)
      .eq("sender_id", user.id);

    if (error) {
      // Roll back on failure
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId && previous ? previous : m))
      );
      console.error("Error editing message:", error);
      throw error;
    }
  };

  // Delete for everyone: sender marks the message deleted; both sides see a placeholder
  const deleteForEveryone = async (messageId: string) => {
    if (!user) return;
    const previous = messages.find((m) => m.id === messageId);
    const deletedAt = new Date().toISOString();

    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, deleted_at: deletedAt } : m))
    );

    const { error } = await supabase
      .from("direct_messages")
      .update({ deleted_at: deletedAt } as any)
      .eq("id", messageId)
      .eq("sender_id", user.id);

    if (error) {
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId && previous ? previous : m))
      );
      console.error("Error deleting message:", error);
      throw error;
    }
  };

  // Delete for me: hide the message from my view only
  const deleteForMe = async (messageId: string) => {
    if (!user) return;
    setMessages((prev) => prev.filter((m) => m.id !== messageId));

    const { error } = await supabase.rpc("hide_direct_message", {
      p_message_id: messageId,
    } as any);

    if (error) {
      console.error("Error hiding message:", error);
      fetchMessages();
      throw error;
    }
  };

  return { messages, loading, sendMessage, editMessage, deleteForEveryone, deleteForMe, markRead, refetch: fetchMessages };
}
